/**
 * RejectOrderDialog — Modal that forces a non-empty reason (≥5 chars) before
 * calling POST /api/orders/{id}/agent-reject. The backend then propagates the
 * motivation via chat + websocket + email (best-effort).
 *
 * Used by AgentApprovals, OrderRowActions, ChatDrawer (replaces window.prompt).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RejectOrderDialog({ open, onClose, order, onRejected }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setSubmitting(false); }
  }, [open]);

  const orderNumber = order?.order_number || order?.id || "—";

  const handleSubmit = async () => {
    const r = (reason || "").trim();
    if (r.length < 5) {
      toast.error("La motivazione deve essere di almeno 5 caratteri");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${order.id}/agent-reject`, { reason: r });
      toast.success(`Ordine ${orderNumber} rifiutato — motivazione inviata al cliente`);
      onRejected?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il rifiuto");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="reject-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="w-5 h-5" />
            Rifiuta ordine {orderNumber}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-800">
            La motivazione verrà inviata al <strong>cliente</strong> + <strong>amministrazione</strong> e
            registrata nella <strong>chat interna</strong> dell&apos;ordine. Inserire un testo chiaro
            (es. articolo non disponibile, prezzo errato, ecc.).
          </div>
          <div>
            <Label htmlFor="reject-reason" className="text-sm font-medium">Motivazione * (minimo 5 caratteri)</Label>
            <Textarea
              id="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Es. Articolo Picanha Wagyu temporaneamente esaurito, ti consiglio il taglio alternativo XYZ..."
              rows={5}
              className="mt-1 text-sm"
              data-testid="reject-reason-input"
              autoFocus
            />
            <p className="text-[11px] text-stone-500 mt-1">{(reason || "").length}/500 caratteri</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} disabled={submitting} data-testid="reject-cancel-btn">
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (reason.trim().length < 5)}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="reject-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
            Rifiuta e invia motivazione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
